import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { swal } from '../utils/swal';
import './Postulaciones.css';

interface Postulacion {
  id: number;
  token: string;
  tipo: string;
  datos_postulado: any;
  estado: string;
  link?: string;
  fecha_expiracion: string;
  created_at?: string;
  acuerdo_firmado: boolean;
  fecha_firma_acuerdo?: string;
  aprobador_nombre?: string;
  rechazador_nombre?: string;
  motivo_rechazo?: string;
  operador_id?: number;
}

const Postulaciones: React.FC = () => {
  const [postulaciones, setPostulaciones] = useState<Postulacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [mostrarModal, setMostrarModal] = useState(false);
  const [postulacionSeleccionada, setPostulacionSeleccionada] = useState<Postulacion | null>(null);
  const [accion, setAccion] = useState<'aprobar' | 'rechazar' | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [mostrarModalDetalles, setMostrarModalDetalles] = useState(false);
  const [postulacionDetalles, setPostulacionDetalles] = useState<Postulacion | null>(null);
  const [vehiculoOperador, setVehiculoOperador] = useState<any>(null);
  const [nuevaPostulacion, setNuevaPostulacion] = useState({
    tipo: 'operador',
    email: '',
    nombre: ''
  });

  useEffect(() => {
    cargarPostulaciones();
  }, [filtroEstado, filtroTipo]);

  const cargarPostulaciones = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroTipo) params.tipo = filtroTipo;

      const response = await api.get('/postulaciones', { params });
      console.log('Postulaciones cargadas:', response.data);
      const postulacionesData = response.data.data || response.data || [];
      setPostulaciones(postulacionesData);
      
      // Si no hay filtros, asegurarse de mostrar todas
      if (postulacionesData.length === 0 && !filtroEstado && !filtroTipo) {
        console.log('No hay postulaciones, pero se intentó cargar sin filtros');
      }
    } catch (error: any) {
      console.error('Error cargando postulaciones:', error);
      console.error('Error response:', error.response?.data);
      await swal.error(error.response?.data?.error || error.message || 'Error al cargar postulaciones');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalCrear = () => {
    setNuevaPostulacion({
      tipo: 'operador',
      email: '',
      nombre: ''
    });
    setMostrarModalCrear(true);
  };

  const cerrarModalCrear = () => {
    setMostrarModalCrear(false);
    setNuevaPostulacion({
      tipo: 'operador',
      email: '',
      nombre: ''
    });
  };

  const crearPostulacion = async () => {
    if (!nuevaPostulacion.email.trim()) {
      await swal.warning('El email es requerido');
      return;
    }

    if (nuevaPostulacion.tipo !== 'operador') {
      await swal.warning('Tipo inválido. Solo se permite: operador');
      return;
    }

    try {
      const response = await api.post('/postulaciones', {
        tipo: nuevaPostulacion.tipo,
        email: nuevaPostulacion.email.trim(),
        nombre: nuevaPostulacion.nombre.trim() || ''
      });

      const link = response.data.link;
      navigator.clipboard.writeText(link);
      await swal.success(`Postulación creada exitosamente!\n\nLink copiado al portapapeles:\n${link}\n\nPuedes enviar este link al postulado para que complete su información.`, '¡Éxito!');
      cerrarModalCrear();
      
      // Recargar lista para mostrar la nueva postulación
      setLoading(true);
      await cargarPostulaciones();
    } catch (error: any) {
      console.error('Error al crear postulación:', error);
      await swal.error(error.response?.data?.error || 'Error al crear postulación');
    }
  };

  const abrirModal = (postulacion: Postulacion, accion: 'aprobar' | 'rechazar') => {
    setPostulacionSeleccionada(postulacion);
    setAccion(accion);
    setObservaciones('');
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setPostulacionSeleccionada(null);
    setAccion(null);
    setObservaciones('');
  };

  const ejecutarAccion = async () => {
    if (!postulacionSeleccionada) return;

    if (accion === 'rechazar' && !observaciones.trim()) {
      await swal.warning('El motivo de rechazo es obligatorio');
      return;
    }

    try {
      if (accion === 'aprobar') {
        console.log('🔍 Aprobando postulación:', postulacionSeleccionada.id);
        const response = await api.post(`/postulaciones/${postulacionSeleccionada.id}/aprobar`, {
          observaciones: observaciones || 'Aprobado correctamente',
          autorizacion_datos: true
        });
        console.log('🔍 Respuesta de aprobación:', response.data);
        await swal.success(`Postulación aprobada exitosamente. Operador creado con ID: ${response.data.operador_id}`);
      } else {
        await api.post(`/postulaciones/${postulacionSeleccionada.id}/rechazar`, {
          motivo_rechazo: observaciones
        });
        await swal.success('Postulación rechazada');
      }
      cerrarModal();
      cargarPostulaciones();
    } catch (error: any) {
      console.error('❌ Error completo al procesar postulación:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.details || 
                          error.response?.data?.message ||
                          error.message || 
                          'Error al procesar la postulación';
      const camposFaltantes = error.response?.data?.campos_faltantes;
      const mensajeCompleto = camposFaltantes 
        ? `${errorMessage}\n\nCampos faltantes: ${camposFaltantes.join(', ')}`
        : errorMessage;
      await swal.error(mensajeCompleto);
    }
  };

  const copiarLink = async (token: string) => {
    const link = `${window.location.origin}/postulacion/${token}`;
    navigator.clipboard.writeText(link);
    await swal.success('Link copiado al portapapeles');
  };

  const verDetalles = async (postulacion: Postulacion) => {
    setPostulacionDetalles(postulacion);
    setVehiculoOperador(null);
    
    // Si la postulación tiene un operador_id, obtener el vehículo del operador
    if (postulacion.operador_id) {
      try {
        const operadorResponse = await api.get(`/operadores/${postulacion.operador_id}`);
        if (operadorResponse.data.vehiculo) {
          setVehiculoOperador(operadorResponse.data.vehiculo);
        }
      } catch (error) {
        console.error('Error obteniendo vehículo del operador:', error);
      }
    }
    
    setMostrarModalDetalles(true);
  };

  const cerrarModalDetalles = () => {
    setMostrarModalDetalles(false);
    setPostulacionDetalles(null);
  };

  const eliminarPostulacion = async (id: number) => {
    const result = await swal.confirm(
      '¿Estás seguro de que deseas eliminar esta postulación? Esta acción no se puede deshacer.',
      'Eliminar Postulación'
    );
    
    if (!result.isConfirmed) {
      return;
    }

    try {
      await api.delete(`/postulaciones/${id}`);
      await swal.success('Postulación eliminada correctamente');
      cargarPostulaciones();
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al eliminar postulación');
    }
  };

  const getEstadoBadge = (estado: string) => {
    const badges: { [key: string]: { text: string; class: string } } = {
      pendiente: { text: 'Pendiente', class: 'badge-warning' },
      completado: { text: 'Completado', class: 'badge-info' },
      aprobado: { text: 'Aprobado', class: 'badge-success' },
      rechazado: { text: 'Rechazado', class: 'badge-danger' }
    };
    const badge = badges[estado] || { text: estado, class: 'badge-secondary' };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div className="loading">Cargando postulaciones...</div>;
  }

  return (
    <div className="postulaciones-page">
      <div className="page-header">
        <div className="header-actions">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="completado">Completado</option>
            <option value="aprobado">Aprobado</option>
            <option value="rechazado">Rechazado</option>
          </select>

          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los tipos</option>
            <option value="operador">Operador</option>
            <option value="vehiculo">Vehículo</option>
          </select>

          <button onClick={abrirModalCrear} className="btn-primary">
            ➕ Nueva Postulación
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tipo</th>
              <th>Postulado</th>
              <th>Email</th>
              <th>Estado</th>
              <th>Acuerdo Firmado</th>
              <th>Fecha Creación</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {postulaciones.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  No hay postulaciones registradas
                </td>
              </tr>
            ) : (
              postulaciones.map((post) => {
                return (
                  <tr key={post.id}>
                    <td>{post.id}</td>
                    <td>
                      <span className="badge badge-info">
                        {post.tipo === 'operador' ? '👤 Operador/Vehículo' : '👤 Operador/Vehículo'}
                      </span>
                    </td>
                    <td>
                      {post.datos_postulado?.nombres && post.datos_postulado?.apellidos
                        ? `${post.datos_postulado.nombres} ${post.datos_postulado.apellidos}`
                        : post.datos_postulado?.nombre || 'Sin nombre'}
                    </td>
                    <td>{post.datos_postulado?.email || 'N/A'}</td>
                    <td>{getEstadoBadge(post.estado || 'pendiente')}</td>
                    <td>
                      {post.acuerdo_firmado ? (
                        <span className="badge badge-success">✓ Firmado</span>
                      ) : (
                        <span className="badge badge-warning">Pendiente</span>
                      )}
                    </td>
                    <td>{new Date(post.fecha_expiracion || post.created_at || Date.now()).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => verDetalles(post)}
                        className="btn-link"
                        title="Ver detalles"
                      >
                        👁️ Ver
                      </button>
                      {(post.estado === 'pendiente' || !post.estado) && (
                        <button
                          onClick={() => copiarLink(post.token)}
                          className="btn-link"
                          title="Copiar link"
                        >
                          🔗 Copiar URL
                        </button>
                      )}
                      {(() => {
                        const estado = post.estado?.toLowerCase() || '';
                        const acuerdoFirmado = post.acuerdo_firmado === true || 
                                               (typeof post.acuerdo_firmado === 'number' && post.acuerdo_firmado === 1) ||
                                               (typeof post.acuerdo_firmado === 'string' && post.acuerdo_firmado === '1');
                        const puedeAprobar = (estado === 'completado' || estado === 'completo') && acuerdoFirmado;
                        
                        if (puedeAprobar) {
                          return (
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                onClick={() => abrirModal(post, 'aprobar')}
                                className="btn-success"
                                title="Aprobar postulación"
                              >
                                ✓ Aprobar
                              </button>
                              <button
                                onClick={() => abrirModal(post, 'rechazar')}
                                className="btn-danger"
                                title="Rechazar postulación"
                              >
                                ✗ Rechazar
                              </button>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {post.estado === 'aprobado' && post.operador_id && (
                        <a
                          href={`/operadores/${post.operador_id}`}
                          className="btn-link"
                        >
                          Ver Operador
                        </a>
                      )}
                      {(post.estado === 'pendiente' || post.estado === 'completado' || post.estado === 'rechazado') && (
                        <button
                          onClick={() => eliminarPostulacion(post.id)}
                          className="btn-danger-small"
                          title="Eliminar postulación"
                        >
                          🗑️ Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {mostrarModal && postulacionSeleccionada && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {accion === 'aprobar' ? 'Aprobar Postulación' : 'Rechazar Postulación'}
            </h2>
            <div className="modal-info">
              <p><strong>Tipo:</strong> {postulacionSeleccionada.tipo}</p>
              <p><strong>Postulado:</strong> {
                postulacionSeleccionada.datos_postulado?.nombres && postulacionSeleccionada.datos_postulado?.apellidos
                  ? `${postulacionSeleccionada.datos_postulado.nombres} ${postulacionSeleccionada.datos_postulado.apellidos}`
                  : postulacionSeleccionada.datos_postulado?.nombre || 'N/A'
              }</p>
              <p><strong>Email:</strong> {postulacionSeleccionada.datos_postulado?.email || 'N/A'}</p>
            </div>
            <div className="form-group">
              <label>
                {accion === 'aprobar' ? 'Observaciones (opcional)' : 'Motivo de Rechazo *'}
              </label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                rows={4}
                placeholder={
                  accion === 'aprobar'
                    ? 'Observaciones sobre la aprobación...'
                    : 'Ingrese el motivo del rechazo...'
                }
                required={accion === 'rechazar'}
              />
            </div>
            <div className="modal-actions">
              <button onClick={cerrarModal} className="btn-secondary">
                Cancelar
              </button>
              <button onClick={ejecutarAccion} className={accion === 'aprobar' ? 'btn-success' : 'btn-danger'}>
                {accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalCrear && (
        <div className="modal-overlay" onClick={cerrarModalCrear}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Crear Nueva Postulación</h2>
            <div className="modal-info">
              <p>Complete los datos para generar un link único que enviará al postulado.</p>
            </div>
            <div className="form-group">
              <label>Tipo de Postulación *</label>
              <select
                value={nuevaPostulacion.tipo}
                onChange={(e) => setNuevaPostulacion({ ...nuevaPostulacion, tipo: e.target.value })}
                className="form-select"
              >
                <option value="operador">👤 Operador/Vehículo</option>
              </select>
            </div>
            <div className="form-group">
              <label>Email del Postulado *</label>
              <input
                type="email"
                value={nuevaPostulacion.email}
                onChange={(e) => setNuevaPostulacion({ ...nuevaPostulacion, email: e.target.value })}
                placeholder="ejemplo@correo.com"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label>Nombre del Postulado (Opcional)</label>
              <input
                type="text"
                value={nuevaPostulacion.nombre}
                onChange={(e) => setNuevaPostulacion({ ...nuevaPostulacion, nombre: e.target.value })}
                placeholder="Nombre completo (opcional)"
                className="form-input"
              />
            </div>
            <div className="modal-actions">
              <button onClick={cerrarModalCrear} className="btn-secondary">
                Cancelar
              </button>
              <button onClick={crearPostulacion} className="btn-primary">
                Crear Postulación
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalDetalles && postulacionDetalles && (
        <div className="modal-overlay" onClick={cerrarModalDetalles}>
          <div className="modal-content modal-detalles" onClick={(e) => e.stopPropagation()}>
            <h2>Detalles de la Postulación #{postulacionDetalles.id}</h2>
            
            <div className="detalles-section">
              <h3>Información General</h3>
              <div className="detalles-grid">
                <div className="detalle-item">
                  <strong>Estado:</strong>
                  <span>{getEstadoBadge(postulacionDetalles.estado)}</span>
                </div>
                <div className="detalle-item">
                  <strong>Tipo:</strong>
                  <span>👤 Operador/Vehículo</span>
                </div>
                <div className="detalle-item">
                  <strong>Email:</strong>
                  <span>{postulacionDetalles.datos_postulado?.email || 'N/A'}</span>
                </div>
                <div className="detalle-item">
                  <strong>Acuerdo Firmado:</strong>
                  <span>
                    {postulacionDetalles.acuerdo_firmado ? (
                      <span className="badge badge-success">✓ Sí</span>
                    ) : (
                      <span className="badge badge-warning">Pendiente</span>
                    )}
                  </span>
                </div>
                <div className="detalle-item">
                  <strong>Fecha de Creación:</strong>
                  <span>{new Date(postulacionDetalles.fecha_expiracion).toLocaleString()}</span>
                </div>
                {postulacionDetalles.fecha_firma_acuerdo && (
                  <div className="detalle-item">
                    <strong>Fecha de Firma:</strong>
                    <span>{new Date(postulacionDetalles.fecha_firma_acuerdo).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {postulacionDetalles.datos_postulado && (
              <div className="detalles-section">
                <h3>Datos del Postulado</h3>
                <div className="detalles-grid">
                  {postulacionDetalles.datos_postulado.nombres && (
                    <div className="detalle-item">
                      <strong>Nombres:</strong>
                      <span>{postulacionDetalles.datos_postulado.nombres}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.apellidos && (
                    <div className="detalle-item">
                      <strong>Apellidos:</strong>
                      <span>{postulacionDetalles.datos_postulado.apellidos}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.tipo_documento && (
                    <div className="detalle-item">
                      <strong>Documento:</strong>
                      <span>
                        {postulacionDetalles.datos_postulado.tipo_documento} {postulacionDetalles.datos_postulado.numero_documento}
                      </span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.telefono && (
                    <div className="detalle-item">
                      <strong>Teléfono:</strong>
                      <span>{postulacionDetalles.datos_postulado.telefono}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.celular && (
                    <div className="detalle-item">
                      <strong>Celular:</strong>
                      <span>{postulacionDetalles.datos_postulado.celular}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.direccion && (
                    <div className="detalle-item full-width">
                      <strong>Dirección:</strong>
                      <span>{postulacionDetalles.datos_postulado.direccion}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.ciudad && (
                    <div className="detalle-item">
                      <strong>Ciudad:</strong>
                      <span>{postulacionDetalles.datos_postulado.ciudad}</span>
                    </div>
                  )}
                  {postulacionDetalles.datos_postulado.departamento && (
                    <div className="detalle-item">
                      <strong>Departamento:</strong>
                      <span>{postulacionDetalles.datos_postulado.departamento}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {postulacionDetalles.datos_postulado?.experiencia_laboral?.length > 0 && (
              <div className="detalles-section">
                <h3>Experiencia Laboral</h3>
                {postulacionDetalles.datos_postulado.experiencia_laboral.map((exp: any, index: number) => (
                  <div key={index} className="experiencia-detalle">
                    <strong>{exp.empresa}</strong> - {exp.cargo}
                    {exp.fecha_inicio && (
                      <div className="fecha">Desde {exp.fecha_inicio} {exp.fecha_fin ? `hasta ${exp.fecha_fin}` : '(actual)'}</div>
                    )}
                    {exp.descripcion && <div className="descripcion">{exp.descripcion}</div>}
                  </div>
                ))}
              </div>
            )}

            {postulacionDetalles.datos_postulado?.estudios?.length > 0 && (
              <div className="detalles-section">
                <h3>Estudios</h3>
                {postulacionDetalles.datos_postulado.estudios.map((estudio: any, index: number) => (
                  <div key={index} className="estudio-detalle">
                    <strong>{estudio.titulo}</strong> - {estudio.institucion}
                    {estudio.fecha_inicio && (
                      <div className="fecha">
                        {estudio.fecha_inicio} {estudio.fecha_fin ? `- ${estudio.fecha_fin}` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {postulacionDetalles.datos_postulado?.referencias?.length > 0 && (
              <div className="detalles-section">
                <h3>Referencias</h3>
                {postulacionDetalles.datos_postulado.referencias.map((ref: any, index: number) => (
                  <div key={index} className="referencia-detalle">
                    <strong>{ref.nombre}</strong>
                    <div>Tel: {ref.telefono}</div>
                    <div>Relación: {ref.relacion}</div>
                  </div>
                ))}
              </div>
            )}

            {postulacionDetalles.estado === 'completado' && (
              <div className="detalles-actions">
                <button
                  onClick={() => {
                    cerrarModalDetalles();
                    abrirModal(postulacionDetalles, 'aprobar');
                  }}
                  className="btn-success"
                >
                  ✓ Aprobar Postulación
                </button>
                <button
                  onClick={() => {
                    cerrarModalDetalles();
                    abrirModal(postulacionDetalles, 'rechazar');
                  }}
                  className="btn-danger"
                >
                  ✗ Rechazar Postulación
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button onClick={cerrarModalDetalles} className="btn-secondary">
                Cerrar
              </button>
              {postulacionDetalles.estado === 'pendiente' && (
                <button
                  onClick={() => {
                    copiarLink(postulacionDetalles.token);
                  }}
                  className="btn-primary"
                >
                  🔗 Copiar Link
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Postulaciones;

